import {
  FolderIcon,
  FolderMinusIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  GridFourIcon,
  ListIcon,
  TagSimpleIcon,
  TrashSimpleIcon,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/shallow";

import { ICON_SIZE } from "@/config/constants";
import { useBoundStore } from "@/app/store";
import { NestedAction } from "@/shared/components/ui/NestedContentNavigator";
import { FolderFormContent, FolderFormValues } from "@/features/folders/components/FolderFormContent";
import { ConfirmDeleteFolderContent } from "@/features/folders/components/ConfirmDeleteFolderContent";
import { FetchedFolder } from "@/services/api/fetchers/folders";

interface MenuItemsOptions {
  onDeleteFolder?: () => void;
  onNewFolder?: (values: FolderFormValues) => void;
  onEditFolder?: (values: FolderFormValues) => void;
  folder: FetchedFolder | null;
  initialValues: FolderFormValues | null;
}

export function useMenuItems(route: "notes" | "todos", options?: MenuItemsOptions): NestedAction[] {
  const navigate = useNavigate();
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const { notesSettings, updateSettings } = useBoundStore(
    useShallow((state) => ({ notesSettings: state.settings.notes, updateSettings: state.settings.updateSettings }))
  );
  const { userId } = useBoundStore(
    useShallow((state) => ({
      userId: state.user.id,
    }))
  );

  if (route === "notes") {
    const menuItems: NestedAction[] = [];

    // Build folder actions
    const folderActions: NestedAction[] = [];

    if (options?.onNewFolder) {
      folderActions.push({
        key: "new-folder",
        label: "New folder",
        icon: <FolderPlusIcon size={ICON_SIZE.md} />,
        content: (
          <FolderFormContent
            initialValues={undefined}
            onSubmit={(values) => {
              options.onNewFolder?.(values);
              navigate(-1);
            }}
            deviceType="mobile"
          />
        ),
      });
    }

    // Add edit folder option only when inside a folder
    if (options?.folder && options?.initialValues && options?.onEditFolder) {
      folderActions.push({
        key: "edit-folder",
        label: "Edit current folder",
        icon: <FolderOpenIcon size={ICON_SIZE.md} />,
        content: (
          <FolderFormContent
            initialValues={options.initialValues}
            onSubmit={(values) => {
              options.onEditFolder?.(values);
              navigate(-1);
            }}
            deviceType="mobile"
          />
        ),
      });
    }

    if (options?.folder && options.onDeleteFolder) {
      folderActions.push({
        key: "delete-folder",
        label: "Delete current folder",
        icon: <FolderMinusIcon size={ICON_SIZE.md} />,
        content: (
          <ConfirmDeleteFolderContent
            isBulkDelete={false}
            folder={options.folder}
            onConfirm={() => {
              options.onDeleteFolder?.();
              navigate(-2);
            }}
            onOpenChange={() => navigate(-1)}
            deviceType="mobile"
          />
        ),
      });
    }

    // Add toggle view option for mobile devices
    if (deviceType === "mobile") {
      menuItems.push({
        key: "toggle-view",
        label: notesSettings.viewMode === "grid" ? "List view" : "Grid view",
        icon:
          notesSettings.viewMode === "grid" ? <ListIcon size={ICON_SIZE.md} /> : <GridFourIcon size={ICON_SIZE.md} />,
        run: async () => {
          if (!userId || !notesSettings) {
            return;
          }

          const updatedViewMode = notesSettings.viewMode === "grid" ? "list" : "grid";
          await updateSettings(userId, { notes: { ...notesSettings, viewMode: updatedViewMode } });
          navigate(-1);
        },
      });
    }

    // Add Folder action with nested actions if there are any
    if (folderActions.length > 0) {
      menuItems.push({
        key: "folder",
        label: "Folder",
        icon: <FolderIcon size={ICON_SIZE.md} />,
        nestedActions: () => folderActions,
      });
    }

    menuItems.push(
      {
        key: "tags",
        label: "Tags",
        icon: <TagSimpleIcon size={ICON_SIZE.md} />,
        run: () => {
          navigate("/tags", { replace: true });
        },
      },
      {
        key: "trash",
        label: "Trash",
        icon: <TrashSimpleIcon size={ICON_SIZE.md} />,
        run: () => {
          navigate("/notes/trash", { replace: true });
        },
      }
    );

    return menuItems;
  }

  return [];
}
